import React, { useState, useEffect, useRef, useContext } from 'react';
import 'katex/dist/katex.min.css';
import '../App.css'
import katex from 'katex';
import { auth, signOut, db } from "../firebase";
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  setDoc
} from "firebase/firestore";

// Create a context for managing flashcards and categories
const FlashcardContext = React.createContext();

// Render LaTeX content
const LatexRenderer = ({ content }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || !content) return;

    // Find all LaTeX expressions and render them
    const regex = /(\$\$[\s\S]*?\$\$|\$[\s\S]*?\$)/g;
    const parts = content.split(regex);

    // Clear the container
    containerRef.current.innerHTML = '';

    parts.forEach((part, index) => {
      if (part.startsWith('$$') && part.endsWith('$$')) {
        // Display math
        const span = document.createElement('div');
        try {
          katex.render(part.slice(2, -2), span, { displayMode: true });
        } catch (e) {
          span.textContent = part;
        }
        containerRef.current.appendChild(span);
      } else if (part.startsWith('$') && part.endsWith('$')) {
        // Inline math
        const span = document.createElement('span');
        try {
          katex.render(part.slice(1, -1), span, { displayMode: false });
        } catch (e) {
          span.textContent = part;
        }
        containerRef.current.appendChild(span);
      } else if (part) {
        // Regular text
        const textNode = document.createTextNode(part);
        containerRef.current.appendChild(textNode);
      }
    });
  }, [content]);

  return <div ref={containerRef} className="latex-content">{content}</div>;
};

// Provider component to manage flashcards and categories state
const FlashcardProvider = ({ children }) => {
  const [flashcards, setFlashcards] = useState([]);
  const [categories, setCategories] = useState([]);

  // Load from Firestore on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const flashcardSnap = await getDocs(collection(db, "flashcards"));
        const loadedFlashcards = flashcardSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setFlashcards(loadedFlashcards);
        localStorage.setItem("mathcards", JSON.stringify(loadedFlashcards));

        const categorySnap = await getDocs(collection(db, "categories"));
        const loadedCategories = categorySnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        const defaultCategories = [
          { id: "default", name: "Sans catégorie" },
        ];

        // If no categories exist in Firebase, initialize them
        if (loadedCategories.length === 0) {
          defaultCategories.forEach(async (cat) => {
            await setDoc(doc(db, "categories", cat.id), cat);
          });
          setCategories(defaultCategories);
          localStorage.setItem("mathcards_categories", JSON.stringify(defaultCategories));
        } else {
          setCategories(loadedCategories);
          localStorage.setItem("mathcards_categories", JSON.stringify(loadedCategories));
        }

      } catch (error) {
        console.error("Failed to load data:", error);
      }
    };

    loadData();
  }, []);

  // Keep localStorage in sync
  useEffect(() => {
    localStorage.setItem("mathcards", JSON.stringify(flashcards));
  }, [flashcards]);

  useEffect(() => {
    localStorage.setItem("mathcards_categories", JSON.stringify(categories));
  }, [categories]);

  // Add, Update, Delete
  const addFlashcard = async (card) => {
    const user = auth.currentUser;

    const cardData = {
      ...card,
      categoryId: card.categoryId || "default",
      uid: user?.uid || null,
      userPhoto: user?.photoURL || null
    };

    const docRef = await addDoc(collection(db, "flashcards"), cardData);
    const newCard = { id: docRef.id, ...cardData };

    setFlashcards([...flashcards, newCard]);
  };

  const updateFlashcard = async (id, updatedCard) => {
    setFlashcards(flashcards.map(card =>
      card.id === id ? { ...card, ...updatedCard } : card
    ));

    const ref = doc(db, "flashcards", id);
    await updateDoc(ref, updatedCard);
  };

  const deleteFlashcard = async (id) => {
    setFlashcards(flashcards.filter(card => card.id !== id));

    const ref = doc(db, "flashcards", id);
    await deleteDoc(ref);
  };

  const addCategory = async (name) => {
    const user = auth.currentUser;

    const newCategory = {
      id: name.toLowerCase().replace(/\s+/g, "-"),
      name,
      uid: user?.uid || null,
      userPhoto: user?.photoURL || null
    };

    setCategories([...categories, newCategory]);
    await setDoc(doc(db, "categories", newCategory.id), newCategory);
    return newCategory;
  };


  const updateCategory = async (id, newName) => {
    setCategories(categories.map(category =>
      category.id === id ? { ...category, name: newName } : category
    ));

    const ref = doc(db, "categories", id);
    await updateDoc(ref, { name: newName });
  };

  const deleteCategory = async (id) => {
    setFlashcards(flashcards.map(card =>
      card.categoryId === id ? { ...card, categoryId: "default" } : card
    ));

    setCategories(categories.filter(category => category.id !== id && category.id !== "default"));

    const ref = doc(db, "categories", id);
    await deleteDoc(ref);
  };

  return (
    <FlashcardContext.Provider
      value={{
        flashcards,
        categories,
        addFlashcard,
        updateFlashcard,
        deleteFlashcard,
        addCategory,
        updateCategory,
        deleteCategory
      }}
    >
      {children}
    </FlashcardContext.Provider>
  );
};

// Custom hook to use the flashcard context
const useFlashcards = () => useContext(FlashcardContext);

// Flashcard Form Component
const FlashcardForm = ({ initialValues, onSubmit, onCancel }) => {
  const [front, setFront] = useState(initialValues?.front || '');
  const [back, setBack] = useState(initialValues?.back || '');
  const [categoryId, setCategoryId] = useState(initialValues?.categoryId || 'default');
  const [image, setImage] = useState(initialValues?.image || '');
  const { categories, addCategory } = useFlashcards();
  const [newCategoryName, setNewCategoryName] = useState('');

  // eslint-disable-next-line
  const [showAddCategory, setShowAddCategory] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (categoryId === "new") {
      alert("Veuillez créer et confirmer le nouveau chapitre avant de valider.");
      return;
    }

    onSubmit({ front, back, categoryId, image });

    if (!initialValues) {
      setFront('');
      setBack('');
      setCategoryId('default');
      setImage('');
    }
  };

  const handleAddCategory = async () => {
    if (newCategoryName.trim()) {
      const newCategory = await addCategory(newCategoryName);
      setCategoryId(newCategory.id);
      setNewCategoryName('');
      setShowAddCategory(false);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 500000) { // ~500kb limit
        alert("Image trop grande ! Veuillez envoyer une image plus légère.");
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  }

  return (
    <form className="card-form" onSubmit={handleSubmit}>
      <div className="form-group">
        <label htmlFor="category">Chapitres</label>
        <div className="category-selector">
          <select
            id="category"
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
            className="category-select"
          >
            {categories.map(category => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
            <option value="new">+ Ajoutez une categorie</option>
          </select>

          {categoryId === 'new' && (
            <div className="add-category-inline">
              <input
                type="text"
                placeholder="Nom du nouveau chapitre"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                className="new-category-input"
              />
              <button
                type="button"
                className="secondary-button"
                onClick={handleAddCategory}
              >
                Ajouter
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Front Side */}
      <div className="form-group">
        <label htmlFor="front">Question (Avant)</label>
        <textarea
          id="front"
          value={front}
          onChange={(e) => setFront(e.target.value)}
          rows="3"
          placeholder="e.g., State Newton's Second Law in mathematical form"
          required
        />
        <p className="hint">Utilisez $ pour les formules en ligne et $$ pour les formules affichées. Exemple : $F = ma$</p>
        <div className="preview">
          <LatexRenderer content={front} />
        </div>
      </div>

      {/* Back Side */}
      <div className="form-group">
        <label htmlFor="back">Réponse (Arrière)</label>
        <textarea
          id="back"
          value={back}
          onChange={(e) => setBack(e.target.value)}
          rows="3"
          placeholder="e.g., $$F = m \cdot a$$"
          required
        />
        <p className="hint">Utilisez $ pour les formules en ligne et $$ pour les formules affichées. Exemple : $F = ma$</p>
        <div className="preview">
          <LatexRenderer content={back} />
        </div>
      </div>

      {/* Image upload */}
      <div className="form-group">
        <label htmlFor="image">Attacher une image (optionnel)</label>
        <input
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          className="image-input"
        />
        {image && (
          <img
            src={image}
            alt="Uploaded"
            style={{
              maxWidth: "100px",
              marginTop: "10px",
              borderRadius: "8px"
            }}
          />
        )}
      </div>

      {/* Buttons */}
      <div className="buttons">
        {onCancel && (
          <button type="button" className="secondary-button" onClick={onCancel}>
            Annuler
          </button>
        )}
        <button type="submit" className="primary-button">
          {initialValues ? 'Mettre à jour' : 'Ajouter'}
        </button>
      </div>
    </form>
  );
};

// Individual Flashcard Component
const Flashcard = ({ card }) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const { updateFlashcard, deleteFlashcard } = useFlashcards();

  // Get current logged-in user from Firebase Auth
  const { currentUser } = auth;
  const isOwner = card.uid === currentUser?.uid;
  const userPhoto = card.userPhoto;

  const handleClick = () => {
    if (!isEditing) setIsFlipped(!isFlipped);
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    setIsEditing(true);
  };

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (window.confirm("Êtes-vous sûr de vouloir supprimer cette carte ?")) {
      await deleteFlashcard(card.id);
    }
  };

  const handleUpdate = async (updatedCard) => {
    await updateFlashcard(card.id, updatedCard);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <FlashcardForm
        initialValues={card}
        onSubmit={handleUpdate}
        onCancel={() => setIsEditing(false)}
      />
    );
  }

  return (
    <div className={`flashcard ${isFlipped ? "flipped" : ""}`} onClick={handleClick}>
      <div className="card-inner">
        <div className="card-front">
          <LatexRenderer content={card.front} />

          {/* Only show buttons if the user owns the card */}

          <div className="card-actions">
            {isOwner && (
              <div>
                <button className="icon-button" onClick={handleEdit}>✏️</button>
                <button className="icon-button" onClick={handleDelete}>🗑️</button>
              </div>
            )}

            {!isOwner && userPhoto && (
              <img
                src={userPhoto}
                alt="User"
                className="user-avatar"
                style={{
                  width: "24px",
                  height: "24px",
                  borderRadius: "50%",
                  position: "absolute",
                  bottom: "0px",
                  right: "8px"
                }}
              />
            )}
          </div>

        </div>
        <div className="card-back">
          <LatexRenderer content={card.back} />
          {card.image && (
            <img
              src={card.image}
              alt="Flashcard visual"
              style={{
                maxWidth: "90%",
                marginTop: "10px",
                borderRadius: "8px",
                objectFit: "contain"
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// Category Management Component
const CategoryManager = () => {
  const { categories, deleteCategory, updateCategory, addCategory } = useFlashcards();
  const [newCategoryName, setNewCategoryName] = useState('');
  const [editingCategory, setEditingCategory] = useState(null);
  const [className, setClassName] = useState("Maths"); // default value
  const [editName, setEditName] = useState('');

  const handleAddCategory = async () => {
    if (newCategoryName.trim()) {
      const newCategory = await addCategory(newCategoryName);
      await setDoc(doc(db, "categories", newCategory.id), {
        ...newCategory,
        class: className
      });

      setNewCategoryName('');
    }
  };


  const startEdit = (category) => {
    setEditingCategory(category.id);
    setEditName(category.name);
  };

  const handleUpdate = () => {
    if (editName.trim()) {
      updateCategory(editingCategory, editName);
      setEditingCategory(null);
    }
  };

  return (
    <div className="category-manager">
      <h2>Gérer les chapitres</h2>

      {/* Add new category */}
      <div className="add-category">
        <input
          type="text"
          placeholder="Nom du nouveau chapitre"
          value={newCategoryName}
          onChange={(e) => setNewCategoryName(e.target.value)}
          className="new-category-input"
        />
        <select
          className="class-dropdown"
          value={className}
          onChange={(e) => setClassName(e.target.value)}
        >
          <option value="Maths">Maths</option>
          <option value="Physics">Physique</option>
          <option value="Chemistry">Chimie</option>
          <option value="AA">Analyse avancée I</option>
          <option value="AL">Algèbre linéaire avancée I</option>
          <option value="PH">Physique avancée I</option>
        </select>
        <button
          className="primary-button"
          onClick={handleAddCategory}
          disabled={!newCategoryName.trim()}
        >
          Ajouter le chapitre
        </button>
      </div>

      {/* List of categories */}
      <div className="category-list">
        {categories.map(category => {
          const currentUser = auth.currentUser;
          const isOwner = category.uid === currentUser?.uid;
          const userPhoto = category.userPhoto;

          return (
            <div key={category.id} className="category-item">
              {editingCategory === category.id ? (
                <div className="edit-category">
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                  />
                  <button className="icon-button success" onClick={handleUpdate}>✓</button>
                  <button className="icon-button" onClick={() => setEditingCategory(null)}>✗</button>
                </div>
              ) : (
                <>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <span className="category-name">{category.name}</span>
                    {!isOwner && userPhoto && (
                      <img
                        src={userPhoto}
                        alt="User"
                        className="user-avatar"
                        style={{
                          width: "20px",
                          height: "20px",
                          borderRadius: "50%",
                          marginLeft: "0.5rem"
                        }}
                      />
                    )}
                  </div>

                  <div className="category-actions">
                    {isOwner && category.id !== "default" && (
                      <>
                        <button className="icon-button" onClick={() => startEdit(category)}>✏️</button>
                        <button
                          className="icon-button"
                          onClick={() => {
                            if (window.confirm(`Supprimer le chapitre "${category.name}"? Toutes les cartes seront déplacées dans "Sans catégorie".`)) {
                              deleteCategory(category.id);
                            }
                          }}
                        >
                          🗑️
                        </button>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  )
};

// Browse Cards Component
const BrowseCards = () => {
  const { flashcards, categories } = useContext(FlashcardContext);
  const [selectedClass, setSelectedClass] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const visibleCategories = selectedClass === 'all'
    ? categories
    : categories.filter(cat => cat.class === selectedClass);

  const visibleCategoryIds = visibleCategories.map(cat => cat.id);

  const filteredCards = selectedCategory === 'all'
    ? flashcards.filter(card => visibleCategoryIds.includes(card.categoryId))
    : flashcards.filter(card => card.categoryId === selectedCategory);

  if (flashcards.length === 0) {
    return (
      <div className="empty-state">
        <h2>Aucune carte pour le moment</h2>
        <p>Créez votre premier carte pour commencer!</p>
      </div>
    );
  }

  return (
    <div>
      {/* Class Filter */}
      <div className="form-group">
        <label>Classe:</label>
        <select
          value={selectedClass}
          onChange={(e) => {
            setSelectedClass(e.target.value);
            setSelectedCategory('all'); // reset category when class changes
          }}
        >
          <option value="all">Toutes les classes</option>
          <option value="Maths">Maths</option>
          <option value="Physics">Physique</option>
          <option value="Chemistry">Chimie</option>
          <option value="AA">Analyse avancée I</option>
          <option value="AL">Algèbre linéaire avancée I</option>
          <option value="PH">Physique avancée I</option>
        </select>
      </div>

      {/* Category Filter */}
      <div className="form-group">
        <label>Chapitre:</label>
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
        >
          <option value="all">Tous les chapitres</option>
          {visibleCategories.map(category => (
            <option key={category.id} value={category.id}>{category.name}</option>
          ))}
        </select>
      </div>


      {filteredCards.length === 0 ? (
        <div className="empty-state">
          <h3>Aucune carte dans ce chapitre pour le moment</h3>
        </div>
      ) : (
        <div className="cards-container">
          {filteredCards.map(card => (
            <Flashcard key={card.id} card={card} />
          ))}
        </div>
      )}
    </div>
  );
};

// Study Component
const Study = () => {
  const { flashcards, categories } = useContext(FlashcardContext);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [selectedClass, setSelectedClass] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const visibleCategories = selectedClass === 'all'
    ? categories
    : categories.filter(cat => cat.class === selectedClass);

  const visibleCategoryIds = visibleCategories.map(cat => cat.id);

  const filteredCards = selectedCategory === 'all'
    ? flashcards.filter(card => visibleCategoryIds.includes(card.categoryId))
    : flashcards.filter(card => card.categoryId === selectedCategory);

  const currentCard = filteredCards[currentCardIndex];
  const currentCategory = currentCard
    ? (categories.find(cat => cat.id === currentCard.categoryId)?.name || 'Sans catégorie')
    : '';

  const goToNextCard = () => {
    setIsFlipped(false);
    setTimeout(() => {
      setCurrentCardIndex((prev) => (prev + 1) % filteredCards.length);
    }, 300);
  };

  const goToPrevCard = () => {
    setIsFlipped(false);
    setTimeout(() => {
      setCurrentCardIndex((prev) =>
        prev === 0 ? filteredCards.length - 1 : prev - 1
      );
    }, 300);
  };

  return (
    <div className="study-container">
      <div className="study-header">
        <h2>
          Mode de révision {filteredCards.length > 0 ? `(${currentCardIndex + 1}/${filteredCards.length})` : ''}
        </h2>

        {/* Class Filter */}
        <div className="class-filter">
          <label>Classe:</label>
          <select
            value={selectedClass}
            onChange={(e) => {
              setSelectedClass(e.target.value);
              setSelectedCategory('all'); // reset category when class changes
            }}
          >
            <option value="all">Toutes les classes</option>
            <option value="Maths">Maths</option>
            <option value="Physics">Physique</option>
            <option value="Chemistry">Chimie</option>
          <option value="AA">Analyse avancée I</option>
          <option value="AL">Algèbre linéaire avancée I</option>
          <option value="PH">Physique avancée I</option>
          </select>
        </div>

        {/* Category Filter */}
        <div className="category-filter">
          <label>Chapitre:</label>
          <select
            value={selectedCategory}
            onChange={(e) => {
              setSelectedCategory(e.target.value);
              setCurrentCardIndex(0);
              setIsFlipped(false);
            }}
          >
            <option value="all">Tous les chapitres</option>
            {visibleCategories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>

        </div>
      </div>

      {flashcards.length === 0 ? (
        <div className="empty-state">
          <h2>Aucune carte pour réviser pour le moment</h2>
          <p>Créez une carte pour commencer!</p>
        </div>
      ) : filteredCards.length === 0 ? (
        <div className="empty-state">
          <h2>Aucune carte dans cette cátégorie pour le moment</h2>
          <p>Selectionnez un autre chapitre ou ajoutez des cartes dans celui là.</p>
        </div>
      ) : (
        <>
          <div
            className={`flashcard study-card ${isFlipped ? 'flipped' : ''}`}
            onClick={() => setIsFlipped(!isFlipped)}
          >
            <div className="card-inner">
              <div className="card-front">
                <div className="card-category-badge">{currentCategory}</div>
                <LatexRenderer content={currentCard.front} />
              </div>
              <div className="card-back">
                <LatexRenderer content={currentCard.back} />
                {currentCard.image && (
                  <img
                    src={currentCard.image}
                    alt="Flashcard visual"
                    style={{
                      maxWidth: "90%",
                      marginTop: "10px",
                      borderRadius: "8px",
                      objectFit: "contain"
                    }}
                  />
                )}
              </div>
            </div>
          </div>

          <div className="study-controls">
            <button className="secondary-button" onClick={goToPrevCard}>
              Précédent
            </button>
            <button
              className="primary-button"
              onClick={() => setIsFlipped(!isFlipped)}
            >
              {isFlipped ? 'Show Question' : 'Show Answer'}
            </button>
            <button className="secondary-button" onClick={goToNextCard}>
              Suivant
            </button>
          </div>
        </>
      )}
    </div>
  );
};

// Create Tab Component
const CreateTab = () => {
  const { addFlashcard } = useFlashcards();

  return (
    <div>
      <h2>Créer une nouvelle carte</h2>
      <FlashcardForm onSubmit={addFlashcard} />
    </div>
  );
};

// Main App Component
const Home = () => {
  const [activeTab, setActiveTab] = useState('create');

  const handleLogout = async () => {
    try {
      await signOut(auth);
      console.log("User signed out");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  return (
    <FlashcardProvider>
      <div className="app-container">
        <header className="header">
          <div className="brand">Cartesium</div>
          <div>Une application de Flashcards pour les Maths, la Physique et la Chimie</div>
          <div><button className="logoutButton" onClick={handleLogout}>Quitter</button></div>
        </header>

        <div className="tabs">
          <button
            className={`tab ${activeTab === 'create' ? 'active' : ''}`}
            onClick={() => setActiveTab('create')}
          >
            Créer
          </button>
          <button
            className={`tab ${activeTab === 'browse' ? 'active' : ''}`}
            onClick={() => setActiveTab('browse')}
          >
            Explorer
          </button>
          <button
            className={`tab ${activeTab === 'study' ? 'active' : ''}`}
            onClick={() => setActiveTab('study')}
          >
            Réviser
          </button>
          <button
            className={`tab ${activeTab === 'categories' ? 'active' : ''}`}
            onClick={() => setActiveTab('categories')}
          >
            Chapitres
          </button>
        </div>

        {activeTab === 'create' && <CreateTab />}
        {activeTab === 'browse' && (
          <div>
            <h2>Vos Flashcards</h2>
            <BrowseCards />
          </div>
        )}
        {activeTab === 'study' && <Study />}
        {activeTab === 'categories' && <CategoryManager />}
      </div>
    </FlashcardProvider>
  );
};

export default Home;